import { Injectable } from '@angular/core';
import { setEntities } from '@ngneat/elf-entities';
import { Observable, of, forkJoin } from 'rxjs';
import { switchMap, tap, map, take, finalize } from 'rxjs/operators';
import { ImportService } from '@tcc-mono/classifact/shared/data-access/classifact-api';
import { Import, ImportResource, ImportTemplate } from '@tcc-mono/classifact/shared/data-access/models';
import { CoreRepository } from '@tcc-mono/shared/data-access';
import { getAllEntities } from '@ngneat/elf-entities';
import { ElfUtil } from '@tcc-mono/shared/utils';
import { throwError,catchError } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ImportRepository extends CoreRepository<Import> {
  constructor(protected _importService: ImportService) {
    super({ name: 'imports' }, _importService);
  }

  public clear(): void {
    // setEntities([]) verwijdert álle items uit de store
    this._store.update(setEntities([]));
  }


  /**
   * Haal alle pagina's op en sync de store met de volledige lijst
   */
  public override listenToParams(): Observable<Import[]> {
    return this.params$.pipe(
      switchMap(params => {
        // begin altijd bij pagina 1 om meta te bepalen
        const firstParams = { ...params, page: 1 };
        return this._importService.getAll(firstParams).pipe(
          switchMap(firstRes => {
            const dataPages: Observable<Import[]>[] = [of(firstRes.data)];
            // let op: meta.last_page, niet lastPage
            const totalPages = firstRes.meta?.last_page ?? 1;
            for (let p = 2; p <= totalPages; p++) {
              dataPages.push(
                this._importService.getAll({ ...firstParams, page: p }).pipe(
                  map(res => res.data)
                )
              );
            }
            return forkJoin(dataPages).pipe(
              map(pages =>
                pages.reduce((acc, arr) => acc.concat(arr), [] as Import[])
              )
            );
          }),
          tap(allData => {
            // sync store: vervang met volledige lijst
            this._updateEntities(allData, { merge: false }, allData);
          })
        );
      })
    );
  }

  /**
   * Verwijdert één import uit de in-memory lijst
   */
  public removeFromList(id: string): void {
  this.entities$.pipe(take(1)).subscribe((list: Import[]) => {
    const updated = list.filter(item => item.id !== id);
    this._updateEntities(updated, { merge: false }, updated);
  });
}


  /**
   * Ophalen van één import, met loading-indicator
   */
  public getImportById(id: string): Observable<Import> {
  this._loading.next(true);
  return this._importService.getById(id).pipe(
    catchError(err => {
      if (err.status === 404) {
        console.warn('Import niet gevonden (404):', id);
        return of(null);
      }
      return throwError(() => err);
    }),
    finalize(() => this._loading.next(false))
  );
}

  /**
   * Haal import progress op voor real-time updates
   */
  public getImportProgress(id: string): Observable<any> {
    return this._importService.getProgress(id);
  }


  public override scrollLoad(event: any): void {
  const pagination = this._pagination.value;
  
  if (!pagination || pagination.currentPage >= pagination.lastPage) {
    event.target.complete(); // 
    return;
  }
  const nextPage = pagination.currentPage + 1;
  const params = {
    ...this._params.value,
    page: nextPage
  };

  this._loading.next(true);
  this._importService.getAll(params).pipe(
    take(1),
    tap(res => {
      const newData = res.data;
      const current = this._store.query(getAllEntities());
      this._updateEntities(current, { merge: true }, newData);
      if (res.meta) {
        this._pagination.next(ElfUtil.convertMetaToElfPagination(res.meta)); // update pagina-info
      }
    }),
    finalize(() => {
      this._loading.next(false);
    })
  ).subscribe();
}


  /**
   * Creëert een nieuwe import
   */
  public postImport(
    file: File,
    collection_name: string,
    template: ImportTemplate,
    remarks: string,
    resource: ImportResource
  ): Observable<Import> {
    this._loading.next(true);
    return this._importService
      .postImport(file, collection_name, template, remarks, resource)
      .pipe(
        map(({ data }) => data),
        tap(imp => this.setActiveEntity(imp.id)),
        finalize(() => this._loading.next(false))
      );
  }

  
}
